import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, apiFetch } from '../../lib/api';
import { AlertTriangle, ExternalLink, Database, Plus, X, User, Key, Loader2, CreditCard, Receipt, Sparkles } from 'lucide-react';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export function formatUsd(v: number): string {
  if (v > 0 && v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<any | null>(null);
  const [proxyConfig, setProxyConfig] = useState<string | null>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  // Re-provision state
  const [showReprovision, setShowReprovision] = useState(false);
  const [reprovisionPassword, setReprovisionPassword] = useState('');
  const [reprovisionLoading, setReprovisionLoading] = useState(false);
  const [reprovisionResult, setReprovisionResult] = useState<{ ok?: boolean; error?: string; adminCreated?: boolean } | null>(null);

  // Usage/storage state
  const [usageData, setUsageData] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Claude API usage & cost state
  const [aiUsage, setAiUsage] = useState<any>(null);
  const [aiEvents, setAiEvents] = useState<any[]>([]);
  const [aiUsageLoaded, setAiUsageLoaded] = useState(false);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);

  // Per-tenant snapshots & backups
  const [tenantSnapshots, setTenantSnapshots] = useState<any[]>([]);
  const [tenantBackups, setTenantBackups] = useState<any[]>([]);
  const [snapshotsLoaded, setSnapshotsLoaded] = useState(false);
  const [backupsLoaded, setBackupsLoaded] = useState(false);
  const [deletingSnapshots, setDeletingSnapshots] = useState(false);
  const [deletingBackup, setDeletingBackup] = useState<string | null>(null);

  // Tenant info editing state
  const [editingInfo, setEditingInfo] = useState(false);
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [infoSaving, setInfoSaving] = useState(false);

  // Domain management state
  const [editingDomains, setEditingDomains] = useState(false);
  const [domainsList, setDomainsList] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainSaveResult, setDomainSaveResult] = useState<{ warnings?: string[] } | null>(null);

  // Tenant users state
  interface TenantUser {
    id: string;
    email: string;
    name: string;
    status: boolean;
    registration: string;
    accessedAt: string;
    role?: string | null;
  }
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [tenantUsersLoading, setTenantUsersLoading] = useState(false);
  const [tenantUsersLoaded, setTenantUsersLoaded] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState<{ ok?: boolean; error?: string } | null>(null);

  // Legacy import state
  const [showLegacyImport, setShowLegacyImport] = useState(false);
  const [legacySqlFile, setLegacySqlFile] = useState<File | null>(null);
  const [legacyMinioUrl, setLegacyMinioUrl] = useState('http://192.168.20.75:8010');
  const [legacyMinioAccessKey, setLegacyMinioAccessKey] = useState('');
  const [legacyMinioSecretKey, setLegacyMinioSecretKey] = useState('');
  const [legacyImportLoading, setLegacyImportLoading] = useState(false);
  const [legacyImportMessages, setLegacyImportMessages] = useState<string[]>([]);
  const [legacyImportResult, setLegacyImportResult] = useState<{ ok?: boolean; error?: string } | null>(null);
  const legacyMessagesEndRef = useRef<HTMLDivElement>(null);

  // Billing history state
  interface BillingRecord {
    id: string;
    tenant_id: string;
    plan: string;
    amount: number;
    currency: string;
    status: 'pending' | 'paid' | 'failed' | 'refunded';
    payment_provider: string;
    payment_id: string;
    description: string;
    created_at: string;
  }
  const [billingHistory, setBillingHistory] = useState<BillingRecord[]>([]);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [planChangeLoading, setPlanChangeLoading] = useState(false);
  const [planChangeResult, setPlanChangeResult] = useState<{ ok?: boolean; error?: string } | null>(null);

  async function loadBillingHistory() {
    if (!id) return;
    setBillingLoading(true);
    try {
      const data = await api.get<{ records: BillingRecord[] }>(`/billing/history/${id}`);
      setBillingHistory(data.records);
      setBillingLoaded(true);
    } catch {
      // billing_history collection may not exist yet
      setBillingLoaded(true);
    } finally {
      setBillingLoading(false);
    }
  }

  async function loadAiUsage() {
    if (!id) return;
    setAiUsageLoading(true);
    try {
      const data = await api.get<any>(`/ai-usage/${id}`);
      setAiUsage(data);
      setAiUsageLoaded(true);
      try {
        const ev = await api.get<{ events: any[] }>(`/ai-usage/${id}/events?limit=50`);
        setAiEvents(ev.events || []);
      } catch {
        setAiEvents([]); // design_events may not exist yet
      }
    } catch {
      // design_usage collection may not exist yet (assistant never used)
      setAiUsageLoaded(true);
    } finally {
      setAiUsageLoading(false);
    }
  }

  async function handleAdminPlanChange(newPlan: string) {
    if (!id || !tenant) return;
    setPlanChangeLoading(true);
    setPlanChangeResult(null);
    try {
      await api.put(`/tenants/${id}`, { plan: newPlan });
      setTenant({ ...tenant, plan: newPlan });
      setPlanChangeResult({ ok: true });
      // Refresh billing history if loaded
      if (billingLoaded) {
        loadBillingHistory();
      }
    } catch (err: any) {
      setPlanChangeResult({ error: err.message || 'Plan wijzigen mislukt' });
    } finally {
      setPlanChangeLoading(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    api.get<any>(`/tenants/${id}`).then(setTenant).catch(e => setError(e.message)).finally(() => setIsLoading(false));
  }, [id]);

  async function doAction(action: string) {
    if (!id) return;
    setActionLoading(action);
    try {
      await api.post(`/tenants/${id}/${action}`);
      const updated = await api.get<any>(`/tenants/${id}`);
      setTenant(updated);
    } catch (e: any) { setError(e.message); }
    finally { setActionLoading(''); }
  }

  async function loadProxyConfig() {
    if (!id) return;
    try {
      const data = await api.get<{ text: string }>(`/tenants/${id}/proxy-config`);
      setProxyConfig(data.text);
    } catch (e: any) { setError(e.message); }
  }

  async function handleReprovision() {
    if (!id) return;
    setReprovisionLoading(true);
    setReprovisionResult(null);
    try {
      const result = await api.post<{ ok: boolean; adminCreated: boolean }>(`/tenants/${id}/reprovision`, {
        admin_password: reprovisionPassword || undefined,
      });
      setReprovisionResult({ ok: true, adminCreated: result.adminCreated });
      // Refresh tenant data
      const updated = await api.get<any>(`/tenants/${id}`);
      setTenant(updated);
    } catch (e: any) {
      setReprovisionResult({ error: e.message });
    } finally {
      setReprovisionLoading(false);
    }
  }

  async function handleLegacyImport() {
    if (!id || !legacySqlFile) return;
    setLegacyImportLoading(true);
    setLegacyImportResult(null);
    setLegacyImportMessages([]);
    try {
      const formData = new FormData();
      formData.append('sqlDump', legacySqlFile);
      formData.append('minio_url', legacyMinioUrl);
      formData.append('minio_access_key', legacyMinioAccessKey);
      formData.append('minio_secret_key', legacyMinioSecretKey);
      const result = await apiFetch<{ ok: boolean; messages?: string[] }>(`/tenants/${id}/import-legacy`, {
        method: 'POST',
        body: formData,
      });
      if (result.messages) {
        setLegacyImportMessages(result.messages);
      }
      setLegacyImportResult({ ok: true });
      // Refresh tenant data
      const updated = await api.get<any>(`/tenants/${id}`);
      setTenant(updated);
    } catch (e: any) {
      setLegacyImportResult({ error: e.message });
    } finally {
      setLegacyImportLoading(false);
    }
  }

  async function handleCopy() {
    if (!proxyConfig) return;
    await navigator.clipboard.writeText(proxyConfig);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function loadTenantUsers() {
    if (!id) return;
    setTenantUsersLoading(true);
    try {
      const data = await api.get<{ users: TenantUser[] }>(`/tenants/${id}/users`);
      setTenantUsers(data.users);
      setTenantUsersLoaded(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTenantUsersLoading(false);
    }
  }

  async function changeUserRole(userId: string, role: 'owner' | 'editor') {
    try {
      await api.patch(`/tenants/${id}/users/${userId}/role`, { role });
      await loadTenantUsers();
    } catch (e: any) {
      alert(`Kon rol niet wijzigen: ${e.message}`);
    }
  }

  async function handleResetPassword() {
    if (!id || !resetPasswordUserId) return;
    setResetPasswordLoading(true);
    setResetPasswordResult(null);
    try {
      await api.patch(`/tenants/${id}/users/${resetPasswordUserId}/password`, { password: resetPasswordValue });
      setResetPasswordResult({ ok: true });
      setResetPasswordValue('');
      setTimeout(() => { setResetPasswordUserId(null); setResetPasswordResult(null); }, 2000);
    } catch (e: any) {
      setResetPasswordResult({ error: e.message });
    } finally {
      setResetPasswordLoading(false);
    }
  }

  if (isLoading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />)}</div>;
  if (!tenant) return <div className="p-6 bg-red-50 rounded-lg text-sm text-red-700">{error || 'Tenant niet gevonden.'}</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/admin/tenants" className="text-brand-600 hover:underline">Tenants</Link>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 font-medium">{tenant.name}</span>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card p-6 lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Gegevens</h2>
            {!editingInfo ? (
              <button
                onClick={() => { setEditingInfo(true); setEditName(tenant.name); setEditSlug(tenant.slug); }}
                className="text-xs text-brand-600 hover:underline"
              >
                Wijzigen
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingInfo(false)}
                  className="text-xs text-gray-500 hover:underline"
                >
                  Annuleren
                </button>
                <button
                  onClick={async () => {
                    setInfoSaving(true);
                    try {
                      await api.put(`/tenants/${id}`, { name: editName, slug: editSlug });
                      setTenant({ ...tenant, name: editName, slug: editSlug });
                      setEditingInfo(false);
                    } catch (err: any) {
                      setError(err.message || 'Opslaan mislukt');
                    } finally {
                      setInfoSaving(false);
                    }
                  }}
                  disabled={infoSaving || !editName.trim() || !editSlug.trim()}
                  className="text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 px-3 py-1 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {infoSaving ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            )}
          </div>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            {editingInfo ? (
              <>
                <div>
                  <dt className="text-xs text-gray-500 uppercase">Naam</dt>
                  <dd className="mt-1"><input value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" /></dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500 uppercase">Slug</dt>
                  <dd className="mt-1"><input value={editSlug} onChange={(e) => setEditSlug(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" /></dd>
                </div>
              </>
            ) : (
              <>
                <div><dt className="text-xs text-gray-500 uppercase">Naam</dt><dd className="mt-1 text-gray-900">{tenant.name}</dd></div>
                <div><dt className="text-xs text-gray-500 uppercase">Slug</dt><dd className="mt-1 text-gray-900">{tenant.slug}</dd></div>
              </>
            )}
            <div><dt className="text-xs text-gray-500 uppercase">Status</dt><dd className="mt-1 text-gray-900">{tenant.status}</dd></div>
            <div>
              <dt className="text-xs text-gray-500 uppercase">Omgeving</dt>
              <dd className="mt-1">
                <select
                  value={tenant.environment || 'prod'}
                  onChange={async (e) => {
                    const environment = e.target.value;
                    const prev = tenant.environment || 'prod';
                    setTenant({ ...tenant, environment });
                    try {
                      await api.put(`/tenants/${id}`, { environment });
                    } catch (err: any) {
                      setTenant({ ...tenant, environment: prev });
                      setError(err.message || 'Omgeving wijzigen mislukt');
                    }
                  }}
                  className="input text-sm"
                >
                  <option value="prod">Productie</option>
                  <option value="test">Test</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Test routeert naar de test-deploy</p>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 uppercase">Opslag</dt>
              <dd className="mt-1 flex items-center gap-2">
                <span className="text-gray-900">{formatBytes(tenant.storage_used_bytes || 0)}</span>
                <button
                  onClick={async () => {
                    setUsageLoading(true);
                    try {
                      const data = await api.get<any>(`/tenants/${id}/usage`);
                      setUsageData(data);
                      setTenant({ ...tenant, storage_used_bytes: data.total_storage_bytes });
                    } catch (e: any) { setError(e.message); }
                    finally { setUsageLoading(false); }
                  }}
                  disabled={usageLoading}
                  className="text-xs text-brand-600 hover:underline"
                >
                  {usageLoading ? 'Berekenen...' : 'Herberekenen'}
                </button>
              </dd>
            </div>
            <div><dt className="text-xs text-gray-500 uppercase">Aangemaakt</dt><dd className="mt-1 text-gray-900">{new Date(tenant.created_at).toLocaleDateString('nl-NL')}</dd></div>
            <div>
              <dt className="text-xs text-gray-500 uppercase">Plan</dt>
              <dd className="mt-1">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium capitalize">
                  <CreditCard className="w-3.5 h-3.5 text-gray-400" />
                  {tenant.plan || 'free'}
                </span>
              </dd>
            </div>
          </dl>

          {/* Usage details */}
          {usageData && (
            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-xs text-gray-500 uppercase mb-3">Gebruik details</h3>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Bestanden</div>
                  <div className="text-lg font-semibold text-gray-900">{usageData.total_files}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Documenten</div>
                  <div className="text-lg font-semibold text-gray-900">{usageData.total_documents}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Opslag</div>
                  <div className="text-lg font-semibold text-gray-900">{formatBytes(usageData.total_storage_bytes)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {Object.entries(usageData.buckets as Record<string, { files: number; bytes: number }>).map(([name, b]) => (
                  <div key={name} className="flex justify-between text-xs text-gray-600 py-1">
                    <span>{name}</span>
                    <span>{b.files} bestanden ({formatBytes(b.bytes)})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Domain management */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <dt className="text-xs text-gray-500 uppercase">Domeinen</dt>
              {!editingDomains ? (
                <button
                  onClick={() => { setEditingDomains(true); setDomainsList(tenant.domains || []); }}
                  className="text-xs text-brand-600 hover:underline"
                >
                  Wijzigen
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingDomains(false)}
                    className="text-xs text-gray-500 hover:underline"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={async () => {
                      setDomainSaving(true);
                      setDomainSaveResult(null);
                      try {
                        const result = await api.put<{ ok: boolean; warnings?: string[] }>(`/tenants/${id}`, { domains: domainsList });
                        setTenant({ ...tenant, domains: domainsList });
                        setEditingDomains(false);
                        setDomainSaveResult(result);
                      } catch (err: any) {
                        setError(err.message || 'Domeinen opslaan mislukt');
                      } finally {
                        setDomainSaving(false);
                      }
                    }}
                    disabled={domainSaving}
                    className="text-xs text-white bg-brand-600 hover:bg-brand-700 px-2 py-1 rounded disabled:opacity-50"
                  >
                    {domainSaving ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              )}
            </div>
            {!editingDomains ? (
              <div className="space-y-1">
                {(tenant.domains || []).length === 0 && <p className="text-sm text-gray-400">Geen domeinen</p>}
                {(tenant.domains || []).map((d: string, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-900">
                    <span>{d}</span>
                    {i === 0 && <span className="text-xs text-gray-400">(primair)</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {domainsList.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={d}
                      onChange={e => {
                        const val = e.target.value;
                        // Auto-split on comma or space: "a.nl, b.nl" becomes two entries
                        if (val.includes(',') || val.includes(' ')) {
                          const parts = val.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
                          if (parts.length > 1) {
                            const updated = [...domainsList];
                            updated.splice(i, 1, ...parts);
                            setDomainsList(updated);
                            return;
                          }
                        }
                        const updated = [...domainsList];
                        updated[i] = val;
                        setDomainsList(updated);
                      }}
                      className="input flex-1 text-sm"
                      placeholder="voorbeeld.nl"
                    />
                    {i === 0 && <span className="text-xs text-gray-400 whitespace-nowrap">primair</span>}
                    {domainsList.length > 1 && (
                      <button
                        onClick={() => setDomainsList(domainsList.filter((_, j) => j !== i))}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => {
                    if (newDomain.trim()) {
                      setDomainsList([...domainsList, newDomain.trim()]);
                      setNewDomain('');
                    } else {
                      setDomainsList([...domainsList, '']);
                    }
                  }}
                  className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700"
                >
                  <Plus className="w-3 h-3" /> Domein toevoegen
                </button>
              </div>
            )}

            {/* Status after domain save */}
            {domainSaveResult?.warnings && domainSaveResult.warnings.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-amber-600">{domainSaveResult.warnings.join('; ')}</p>
              </div>
            )}
          </div>
        </div>

        <div className="card p-6 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Acties</h2>
          {tenant.domains?.[0] && (
            <a href={`https://${tenant.domains[0]}`} target="_blank" rel="noopener noreferrer"
              className="btn-primary w-full text-sm flex items-center justify-center gap-2">
              <ExternalLink className="w-3.5 h-3.5" /> Site openen
            </a>
          )}
          <hr className="border-gray-200" />
          <button onClick={() => doAction(tenant.status === 'suspended' ? 'reactivate' : 'suspend')}
            disabled={!!actionLoading || tenant.status === 'archived'}
            className={tenant.status === 'suspended' ? 'btn-primary w-full text-sm' : 'btn-secondary w-full text-sm'}>
            {actionLoading === 'suspend' || actionLoading === 'reactivate' ? 'Bezig...' : tenant.status === 'suspended' ? 'Heractiveren' : 'Suspenderen'}
          </button>
          <button onClick={() => doAction('archive')} disabled={!!actionLoading || tenant.status === 'archived'} className="btn-danger w-full text-sm">
            {actionLoading === 'archive' ? 'Bezig...' : 'Archiveren'}
          </button>
          <button onClick={() => setConfirmDelete(true)} disabled={!!actionLoading} className="w-full text-sm px-4 py-2 text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 font-medium transition-colors">
            Verwijderen
          </button>
          <hr className="border-gray-200" />
          <button
            onClick={() => setShowReprovision(!showReprovision)}
            className="btn-secondary w-full text-sm"
          >
            Opnieuw provisionen
          </button>
          <button
            onClick={() => setShowLegacyImport(!showLegacyImport)}
            className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
          >
            <Database className="w-3.5 h-3.5" /> Legacy Import
          </button>
        </div>
      </div>

      {/* Plan management (admin) */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-gray-400" />
          <h2 className="text-base font-semibold text-gray-900">Abonnement beheren</h2>
        </div>
        <p className="text-sm text-gray-500">
          Wijzig het plan van deze tenant handmatig. De tenant wordt direct bijgewerkt.
        </p>

        <div className="flex flex-wrap gap-3">
          {[
            { key: 'free', label: 'Free', price: 'Gratis', color: 'bg-gray-100 text-gray-700 border-gray-200' },
            { key: 'pro', label: 'Pro', price: '\u20AC 9,99/mnd', color: 'bg-brand-50 text-brand-700 border-brand-200' },
            { key: 'business', label: 'Business', price: '\u20AC 24,99/mnd', color: 'bg-purple-50 text-purple-700 border-purple-200' },
          ].map(p => {
            const isCurrent = (tenant.plan || 'free') === p.key;
            return (
              <button
                key={p.key}
                onClick={() => !isCurrent && handleAdminPlanChange(p.key)}
                disabled={isCurrent || planChangeLoading}
                className={`relative px-4 py-3 rounded-lg border text-sm font-medium transition-all disabled:cursor-default ${
                  isCurrent
                    ? `${p.color} ring-2 ring-offset-1 ring-green-500`
                    : `${p.color} hover:shadow-md disabled:opacity-50`
                }`}
              >
                <span className="block font-semibold">{p.label}</span>
                <span className="block text-xs mt-0.5 opacity-75">{p.price}</span>
                {isCurrent && (
                  <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    Actief
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {planChangeLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Plan wordt gewijzigd...
          </div>
        )}
        {planChangeResult?.ok && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
            Plan succesvol gewijzigd.
          </div>
        )}
        {planChangeResult?.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {planChangeResult.error}
          </div>
        )}

        {/* Billing history section */}
        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-gray-400" /> Betalingsgeschiedenis
            </h3>
            {!billingLoaded ? (
              <button onClick={loadBillingHistory} disabled={billingLoading} className="text-xs text-brand-600 hover:underline">
                {billingLoading ? 'Laden...' : 'Geschiedenis laden'}
              </button>
            ) : (
              <button onClick={loadBillingHistory} disabled={billingLoading} className="text-xs text-brand-600 hover:underline">
                Vernieuwen
              </button>
            )}
          </div>

          {billingLoading && !billingLoaded && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          )}

          {billingLoaded && billingHistory.length === 0 && (
            <p className="text-sm text-gray-400">Nog geen betalingen voor deze tenant.</p>
          )}

          {billingLoaded && billingHistory.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Datum</th>
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Omschrijving</th>
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Bedrag</th>
                    <th className="py-2 font-medium text-gray-500 text-xs">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.map(record => (
                    <tr key={record.id} className="border-b border-gray-50">
                      <td className="py-2 pr-4 text-gray-700">{new Date(record.created_at).toLocaleDateString('nl-NL')}</td>
                      <td className="py-2 pr-4 text-gray-900">{record.description}</td>
                      <td className="py-2 pr-4 text-gray-900 font-medium">
                        {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(record.amount / 100)}
                      </td>
                      <td className="py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          record.status === 'paid' ? 'bg-green-100 text-green-700' :
                          record.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          record.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {record.status === 'paid' ? 'Betaald' :
                           record.status === 'pending' ? 'In behandeling' :
                           record.status === 'failed' ? 'Mislukt' :
                           'Terugbetaald'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Claude API usage & cost */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-gray-400" /> Claude API-gebruik & kosten
          </h2>
          <button onClick={loadAiUsage} disabled={aiUsageLoading} className="text-xs text-brand-600 hover:underline">
            {aiUsageLoading ? 'Laden...' : aiUsageLoaded ? 'Vernieuwen' : 'Gebruik laden'}
          </button>
        </div>
        <p className="text-xs text-gray-400">
          Ontwerp-assistent{aiUsage?.model ? ` (model ${aiUsage.model})` : ''}. Kosten zijn een schatting op basis van Claude-tokentarieven.
        </p>

        {aiUsageLoading && !aiUsageLoaded && (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        )}

        {aiUsageLoaded && (!aiUsage || aiUsage.months.length === 0) && (
          <p className="text-sm text-gray-400">Nog geen assistent-gebruik voor deze tenant.</p>
        )}

        {aiUsageLoaded && aiUsage && aiUsage.months.length > 0 && (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">{formatUsd(aiUsage.total_cost_usd)}</span>
              <span className="text-sm text-gray-500">totaal tot nu toe</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Maand</th>
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Berichten</th>
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Input</th>
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Output</th>
                    <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Cache</th>
                    <th className="py-2 font-medium text-gray-500 text-xs">Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {aiUsage.months.map((m: any) => (
                    <tr key={m.period} className="border-b border-gray-50">
                      <td className="py-2 pr-4 text-gray-700">{m.period}</td>
                      <td className="py-2 pr-4 text-gray-700">{m.message_count}</td>
                      <td className="py-2 pr-4 text-gray-700">{formatTokens(m.input_tokens)}</td>
                      <td className="py-2 pr-4 text-gray-700">{formatTokens(m.output_tokens)}</td>
                      <td className="py-2 pr-4 text-gray-700">{formatTokens((m.cache_creation_input_tokens || 0) + (m.cache_read_input_tokens || 0))}</td>
                      <td className="py-2 text-gray-900 font-medium">{formatUsd(m.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {aiEvents.length > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Recente gesprekken</h3>
                <p className="text-xs text-gray-400 mb-3">Alleen metadata — geen berichtinhoud wordt opgeslagen.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 text-left">
                        <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Tijd</th>
                        <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Lengte</th>
                        <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Tools</th>
                        <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Status</th>
                        <th className="py-2 font-medium text-gray-500 text-xs">Kosten</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiEvents.map((e: any, i: number) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{new Date(e.ts).toLocaleString('nl-NL')}</td>
                          <td className="py-2 pr-4 text-gray-700">{e.message_chars} tekens{e.attachments > 0 ? ` +${e.attachments}📎` : ''}</td>
                          <td className="py-2 pr-4 text-gray-600 text-xs">{e.tools && e.tools.length > 0 ? e.tools.join(', ') : '—'}</td>
                          <td className="py-2 pr-4">
                            {e.refusal
                              ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">geweigerd</span>
                              : e.tool_errors > 0
                              ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{e.tool_errors} fout(en)</span>
                              : e.stopped_reason === 'max_iterations'
                              ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">max iteraties</span>
                              : <span className="text-gray-400 text-xs">ok</span>}
                          </td>
                          <td className="py-2 text-gray-900 font-medium">{formatUsd(e.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showReprovision && (
        <div className="card p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900">Opnieuw provisionen</h2>
          <p className="text-sm text-gray-500">
            Hiermee worden ontbrekende collections, buckets en standaard-instellingen opnieuw aangemaakt.
            Bestaande data wordt niet overschreven.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Admin wachtwoord <span className="text-gray-400 font-normal">(optioneel)</span>
            </label>
            <input
              type="password"
              value={reprovisionPassword}
              onChange={e => setReprovisionPassword(e.target.value)}
              placeholder="Laat leeg als de admin user al bestaat"
              className="input w-full"
            />
            <p className="text-xs text-gray-400 mt-1">
              Vul een wachtwoord in om een admin-gebruiker aan te maken in het tenant project.
              Als de gebruiker al bestaat wordt deze stap overgeslagen.
            </p>
          </div>
          <button
            onClick={handleReprovision}
            disabled={reprovisionLoading}
            className="px-5 py-2.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {reprovisionLoading ? 'Bezig met provisionen...' : 'Opnieuw provisionen'}
          </button>
          {reprovisionResult?.ok && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              Provisioning geslaagd.{reprovisionResult.adminCreated ? ' Admin-gebruiker aangemaakt.' : ''}
            </div>
          )}
          {reprovisionResult?.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {reprovisionResult.error}
            </div>
          )}
        </div>
      )}

      {showLegacyImport && (
        <div className="card p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900">Legacy Import (PostgreSQL)</h2>
          <p className="text-sm text-gray-500">
            Importeer data vanuit een oude PostgreSQL database dump met MinIO bestanden.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SQL dump bestand
            </label>
            <input
              type="file"
              accept=".sql"
              onChange={e => setLegacySqlFile(e.target.files?.[0] || null)}
              className="input w-full text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">
              Selecteer een .sql bestand met de PostgreSQL database dump.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MinIO Console URL
            </label>
            <input
              type="text"
              value={legacyMinioUrl}
              onChange={e => setLegacyMinioUrl(e.target.value)}
              placeholder="http://192.168.20.75:8010"
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MinIO Access Key
            </label>
            <input
              type="text"
              value={legacyMinioAccessKey}
              onChange={e => setLegacyMinioAccessKey(e.target.value)}
              placeholder="Access key"
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MinIO Secret Key
            </label>
            <input
              type="password"
              value={legacyMinioSecretKey}
              onChange={e => setLegacyMinioSecretKey(e.target.value)}
              placeholder="Secret key"
              className="input w-full"
            />
          </div>

          <button
            onClick={handleLegacyImport}
            disabled={legacyImportLoading || !legacySqlFile || !legacyMinioAccessKey || !legacyMinioSecretKey}
            className="px-5 py-2.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {legacyImportLoading ? 'Bezig met importeren...' : 'Importeren'}
          </button>

          {legacyImportLoading && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              Bezig met importeren... Dit kan enkele minuten duren.
            </div>
          )}

          {legacyImportMessages.length > 0 && (
            <div className="bg-gray-900 text-green-400 text-xs p-4 rounded-lg overflow-y-auto max-h-60 font-mono">
              {legacyImportMessages.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
              <div ref={legacyMessagesEndRef} />
            </div>
          )}

          {legacyImportResult?.ok && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              Import succesvol voltooid.
            </div>
          )}
          {legacyImportResult?.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {legacyImportResult.error}
            </div>
          )}
        </div>
      )}

      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Proxy Config</h2>
          {!proxyConfig ? (
            <button onClick={loadProxyConfig} className="btn-secondary text-sm">Config laden</button>
          ) : (
            <button onClick={handleCopy} className="btn-secondary text-sm">{copied ? 'Gekopieerd!' : 'Kopieren'}</button>
          )}
        </div>
        {proxyConfig ? (
          <pre className="bg-gray-900 text-green-400 text-xs p-4 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono">{proxyConfig}</pre>
        ) : (
          <p className="text-sm text-gray-400">Klik op "Config laden" om de proxy-configuratie voor Traefixie te bekijken.</p>
        )}
      </div>

      {/* Monitoring & Backups per tenant */}
      <div className="card p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900">Monitoring & Backups</h2>

        <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3 grid grid-cols-3 gap-2">
          <div>Plan: <span className="font-medium text-gray-700 capitalize">{tenant.plan || 'free'}</span></div>
          <div>Monitoring: <span className="font-medium text-gray-700">
            {tenant.plan === 'business' ? 'elke 30 min' : tenant.plan === 'pro' ? 'elk uur' : 'elke 2 uur'}
          </span></div>
          <div>Backup: <span className="font-medium text-gray-700">
            {tenant.plan === 'free' ? 'wekelijks' : 'dagelijks'}
          </span></div>
        </div>

        {/* Snapshots */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Statuschecks</h3>
            <div className="flex gap-2">
              {snapshotsLoaded && tenantSnapshots.length > 0 && (
                <button
                  onClick={async () => {
                    setDeletingSnapshots(true);
                    try {
                      await api.delete(`/monitoring/snapshots/${id}`);
                      setTenantSnapshots([]);
                    } catch (e: any) { setError(e.message); }
                    finally { setDeletingSnapshots(false); }
                  }}
                  disabled={deletingSnapshots}
                  className="text-xs text-red-600 hover:underline"
                >
                  {deletingSnapshots ? 'Wissen...' : 'Alles wissen'}
                </button>
              )}
              <button
                onClick={async () => {
                  try {
                    const data = await api.get<any>(`/monitoring/snapshots/${id}`);
                    setTenantSnapshots(data.snapshots || []);
                    setSnapshotsLoaded(true);
                  } catch (e: any) { setError(e.message); }
                }}
                className="text-xs text-brand-600 hover:underline"
              >
                {snapshotsLoaded ? 'Vernieuwen' : 'Laden'}
              </button>
            </div>
          </div>
          {snapshotsLoaded && (
            tenantSnapshots.length === 0
              ? <p className="text-xs text-gray-400">Geen statuschecks</p>
              : <div className="max-h-48 overflow-y-auto border border-gray-100 rounded">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0"><tr>
                      <th className="px-3 py-1.5 text-left text-gray-500">Tijd</th>
                      <th className="px-3 py-1.5 text-left text-gray-500">Status</th>
                      <th className="px-3 py-1.5 text-left text-gray-500">Responstijd</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {tenantSnapshots.slice(0, 20).map((s: any) => (
                        <tr key={s.id}>
                          <td className="px-3 py-1.5 text-gray-600">{new Date(s.checked_at).toLocaleString('nl-NL')}</td>
                          <td className="px-3 py-1.5">
                            <span className={`inline-block h-2 w-2 rounded-full ${s.is_reachable ? 'bg-green-500' : 'bg-red-500'}`} />
                          </td>
                          <td className="px-3 py-1.5 text-gray-600">{s.response_time_ms > 0 ? `${s.response_time_ms}ms` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
          )}
        </div>

        {/* Backups */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Backups</h3>
            <button
              onClick={async () => {
                try {
                  const data = await api.get<any>(`/backups/${id}`);
                  setTenantBackups(data.backups || []);
                  setBackupsLoaded(true);
                } catch (e: any) { setError(e.message); }
              }}
              className="text-xs text-brand-600 hover:underline"
            >
              {backupsLoaded ? 'Vernieuwen' : 'Laden'}
            </button>
          </div>
          {backupsLoaded && (
            tenantBackups.length === 0
              ? <p className="text-xs text-gray-400">Geen backups</p>
              : <div className="space-y-1">
                  {tenantBackups.slice(0, 10).map((b: any) => (
                    <div key={b.id} className="flex items-center justify-between text-xs py-1.5 px-3 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <span className={b.status === 'completed' ? 'text-green-600' : 'text-red-600'}>●</span>
                        <span className="text-gray-700">{new Date(b.completed_at || b.started_at).toLocaleString('nl-NL')}</span>
                        <span className="text-gray-400">{b.type}</span>
                        {b.size_bytes > 0 && <span className="text-gray-400">{formatBytes(b.size_bytes)}</span>}
                      </div>
                      {b.status === 'failed' && b.error_message && (
                        <span className="text-red-500 truncate max-w-xs" title={b.error_message}>{b.error_message}</span>
                      )}
                    </div>
                  ))}
                </div>
          )}
        </div>
      </div>

      {/* Tenant users */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <User className="w-4 h-4" /> Site gebruikers
          </h2>
          {!tenantUsersLoaded ? (
            <button onClick={loadTenantUsers} disabled={tenantUsersLoading} className="btn-secondary text-sm">
              {tenantUsersLoading ? 'Laden...' : 'Gebruikers laden'}
            </button>
          ) : (
            <button onClick={loadTenantUsers} disabled={tenantUsersLoading} className="text-xs text-brand-600 hover:underline">
              Vernieuwen
            </button>
          )}
        </div>

        {tenantUsersLoading && !tenantUsersLoaded && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        )}

        {tenantUsersLoaded && tenantUsers.length === 0 && (
          <p className="text-sm text-gray-400">Geen gebruikers gevonden in dit project.</p>
        )}

        {tenantUsersLoaded && tenantUsers.length > 0 && (
          <div className="divide-y divide-gray-100">
            {tenantUsers.map(u => (
              <div key={u.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{u.name || u.email}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Geregistreerd: {new Date(u.registration).toLocaleDateString('nl-NL')}
                      {u.accessedAt && ` — Laatst actief: ${new Date(u.accessedAt).toLocaleDateString('nl-NL')}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {u.role && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        u.role === 'admin' || u.role === 'owner'
                          ? 'bg-brand-100 text-brand-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {u.role}
                      </span>
                    )}
                    {u.role === 'editor' && (
                      <button
                        onClick={() => changeUserRole(u.id, 'owner')}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                      >
                        Maak owner
                      </button>
                    )}
                    {u.role === 'owner' && (
                      <button
                        onClick={() => changeUserRole(u.id, 'editor')}
                        className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                      >
                        Maak editor
                      </button>
                    )}
                    <button
                      onClick={() => { setResetPasswordUserId(u.id); setResetPasswordValue(''); setResetPasswordResult(null); }}
                      className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
                    >
                      <Key className="w-3 h-3" /> Wachtwoord
                    </button>
                  </div>
                </div>

                {resetPasswordUserId === u.id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Nieuw wachtwoord voor {u.email}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={resetPasswordValue}
                        onChange={e => setResetPasswordValue(e.target.value)}
                        placeholder="Minimaal 8 tekens"
                        className="input flex-1 text-sm"
                      />
                      <button
                        onClick={handleResetPassword}
                        disabled={resetPasswordLoading || resetPasswordValue.length < 8}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                      >
                        {resetPasswordLoading ? 'Bezig...' : 'Resetten'}
                      </button>
                      <button
                        onClick={() => setResetPasswordUserId(null)}
                        className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Annuleren
                      </button>
                    </div>
                    {resetPasswordResult?.ok && (
                      <p className="text-xs text-green-600">Wachtwoord succesvol gewijzigd.</p>
                    )}
                    {resetPasswordResult?.error && (
                      <p className="text-xs text-red-600">{resetPasswordResult.error}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Tenant verwijderen?</h3>
                <p className="text-sm text-gray-500">Dit kan niet ongedaan worden gemaakt.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Dit verwijdert de tenant <strong>{tenant.name}</strong> permanent uit het platform.
              Het Appwrite project en de data worden <strong>niet</strong> automatisch verwijderd.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Typ <strong>{tenant.name}</strong> om te bevestigen
              </label>
              <input
                type="text"
                value={deleteConfirmName}
                onChange={e => setDeleteConfirmName(e.target.value)}
                className="input w-full"
                placeholder={tenant.name}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setConfirmDelete(false); setDeleteConfirmName(''); }} className="btn-secondary text-sm">
                Annuleren
              </button>
              <button
                onClick={async () => {
                  try {
                    await api.delete(`/tenants/${id}`);
                    navigate('/admin/tenants');
                  } catch (e: any) {
                    setError(e.message);
                    setConfirmDelete(false);
                  }
                }}
                disabled={deleteConfirmName !== tenant.name}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Definitief verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
